import { useMemo } from "react"
import { map, includes, orderBy, get, pick } from "lodash"
import { GMV, REFERENCE_EQUITY } from "gatherers/constants"

export function positionSetHasNoGmvOrEquity(positionSet, metricId = null) {
  if (metricId && metricId !== GMV && metricId !== REFERENCE_EQUITY) {
    return false
  }
  return !includes(["PORTFOLIO", "EXPERIMENT", "INLINE"], positionSet?.type) && !get(positionSet, "dates.0.equity") && !positionSet?.isOptimization
}

function getSortableAbsoluteValue(value) {
  const absValue = Math.abs(value)
  return isNaN(absValue) ? -Infinity : absValue
}

export default function useCompositionTables({
  configKey,
  positionSets,
  model,
  getEffectiveEndDate,
  currency,
  selectedGroup,
  updateQueryString,
  classificationsStatus,
  labelFormatter,
  positionFormatter,
  valueGetter,
  tableValueFormatter,
  availableGroups,
  classifications,
  renderer,
  gatherer,
  rowCount,
  currentDate,
}) {
  const groupsTable = useMemo(() => ({
    id: "groups",
    configKey,
    name: "Groups",
    downloadName: selectedGroup?.label,
    separator: true,
    renderer,
    metrics: map(positionSets, (set, index) => gatherer(model, set.queryPositionSet, set.queryBase, getEffectiveEndDate?.(index) || currentDate, {
      type: "groups",
      currency,
      noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
      groupByOption: selectedGroup,
      ...pick(set, ["isIntradayMode", "intradayEnabled", "batchId"]),
    })),
    labelFormatter,
    valueGetter,
    formatter: tableValueFormatter,
    availableGroups,
    selectedGroup,
    setSelectedGroup: (group) => updateQueryString({ selectedGroup: group }),
    classificationsStatus,
    defaultExpanded: true,
    rowCount,
    orderRowsBy: (rows) => orderBy(rows, ({ economicExposure }) => getSortableAbsoluteValue(economicExposure), "desc"),
  }), [selectedGroup, renderer, positionSets, labelFormatter, valueGetter, tableValueFormatter, availableGroups, classificationsStatus, gatherer, model, currency, updateQueryString, configKey, rowCount, getEffectiveEndDate, currentDate])

  const positionsTable = useMemo(() => ({
    id: "positions",
    configKey: `${configKey}-positions`,
    name: "Positions",
    separator: true,
    renderer,
    metrics: map(positionSets, (set, index) => gatherer(model, set.queryPositionSet, set.queryBase, getEffectiveEndDate?.(index) || currentDate, {
      type: "positions",
      currency,
      noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
      groupByOption: selectedGroup,
      classifications,
      ...pick(set, ["isIntradayMode", "intradayEnabled", "batchId"]),
    })),
    labelFormatter: positionFormatter,
    valueGetter,
    formatter: tableValueFormatter,
    classificationsStatus,
    defaultExpanded: true,
    rowCount,
    orderRowsBy: (rows) => orderBy(rows, "ticker", "asc"),
  }), [renderer, positionSets, positionFormatter, valueGetter, tableValueFormatter, classificationsStatus, gatherer, model, currency, selectedGroup, classifications, configKey, rowCount, getEffectiveEndDate, currentDate])

  return [groupsTable, positionsTable]
}
